import React, { useState } from "react";
import {
    Box,
    Typography,
    Button,
    ImageList,
    ImageListItem,
    useTheme,
    useMediaQuery
} from "@mui/material";
import PageSeo from "../seo/PageSeo";
import PageLayout from "../components/PageLayout";
import { SOCIAL_MAILTO } from "../site/contact";
import { SITE_URL, toAbsoluteImageUrl } from "../seo/constants";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import { Link } from 'react-router-dom';

const puppyImages = [
    { src: "/KaijaPuppy.jpg", alt: "Kaija Standard Poodle Puppy Image 1" },
    { src: "/CalciferPuppy0.jpg", alt: "Calcifer Standard Poodle Puppy Image 1" },
    { src: "/CalciferPuppy01.jpg", alt: "Calcifer Standard Poodle Puppy Image 2" },
    { src: "/CalciferPuppy.jpg", alt: "Calcifer Standard Poodle Puppy Image 3" },
    { src: "/KaijaPuppy2.jpg", alt: "Kaija Standard Poodle Puppy Image 2" },
    { src: "/CalciferPuppy02.jpg", alt: "Calcifer Standard Poodle Puppy Image 4" }
];

const UpcomingLitters: React.FC = () => {
    const [open, setOpen] = useState(false);
    const [index, setIndex] = useState(0);

    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.down("sm"));
    const isSm = useMediaQuery(theme.breakpoints.between("sm", "md"));
    const cols = isXs ? 1 : isSm ? 2 : 3;

    const pageDescription =
        'AKC standard poodle puppies expected Spring 2027 in Austin, TX. Kaija has passed health testing and earned her CHIC number. Contact Galia Standard Poodles for updates.';

    const productImages = puppyImages.map((puppy) => toAbsoluteImageUrl(puppy.src));

    return (
        <>
            <PageSeo
                title="AKC Standard Poodle Puppies for Sale in Austin, TX | Upcoming Litter 2027"
                description={pageDescription}
                path="/upcoming-litters"
                image="/KaijaPuppy.jpg"
                jsonLd={{
                    '@context': 'https://schema.org',
                    '@type': 'Product',
                    name: 'AKC Standard Poodle Puppy - Upcoming Litter 2027',
                    image: productImages,
                    description: pageDescription,
                    brand: {
                        '@type': 'Brand',
                        name: 'Galia Standard Poodles',
                    },
                    offers: {
                        '@type': 'Offer',
                        url: `${SITE_URL}/upcoming-litters`,
                        priceCurrency: 'USD',
                        price: '1500.00',
                        itemCondition: 'https://schema.org/NewCondition',
                        availability: 'https://schema.org/PreOrder',
                    },
                }}
            />

            <PageLayout>
                    <Typography
                        component="h1"
                        variant="h4"
                        gutterBottom
                        sx={{ textAlign: "left", fontSize: { xs: "2rem", sm: "2.5rem" } }}
                    >
                        Upcoming Litter Announcement 2027
                    </Typography>
                    <Typography
                        variant="body1"
                        paragraph
                        sx={{ mb: 2, textAlign: "left" }}
                    >
                        We're excited to announce that Kaija has passed her health testing and has her CHIC number. We are
                        planning a litter of AKC registered standard poodle puppies for Spring 2027, pending the selection
                        of a stud. Depending on the chosen stud, we anticipate that the puppies will primarily be black,
                        with the possibility of a few cream-colored puppies. Kaija is very energetic, and we believe that
                        many of her puppies will do best in active pet or sport homes. We have a few spots left on our
                        waitlist and will be confirming the waitlist soon. For the latest updates and information, please
                        click the "Contact Us" button below.
                    </Typography>

                    <Box sx={{ display: "flex", justifyContent: "center", gap: 2, flexWrap: "wrap" }}>
                        <Button
                            variant="outlined"
                            sx={{
                                padding: "10px 20px",
                                fontSize: { xs: "1rem", sm: "1.2rem" },
                                fontWeight: "bold",
                                borderRadius: 3,
                                borderWidth: 2,
                                borderColor: "primary.main",
                                color: "primary.main",
                                "&:hover": {
                                    borderColor: "primary.dark",
                                    color: "primary.dark",
                                },
                                marginBottom: 2,
                            }}
                            href={SOCIAL_MAILTO}
                        >
                            Contact Us Now
                        </Button>
                    </Box>

                    <Typography
                        variant="body1"
                        sx={{ mb: 2, textAlign: "left" }}
                    >
                        While we await confirmation on the upcoming litter, we invite you to enjoy photos of our adult
                        standard poodles as puppies. Kaija was bred by Donnchada Poodles, and Calcifer comes from Kvali
                        Poodles. Calcifer is now a retired stud and remains a beloved part of our home and program. Please see the <Link to="/our-poodles" style={{ textDecoration: 'underline', color: '#1976d2' }}>Our Poodles</Link> section for more information about Calcifer.
                    </Typography>

                    <Typography
                        component="h2"
                        variant="h4"
                        sx={{ mt: 4, mb: 2, textAlign: "center" }}
                    >
                        Ready to Welcome a Puppy?
                    </Typography>

                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            gap: 2,
                            flexWrap: "wrap",
                            marginBottom: 4,
                        }}
                    >
                        <Button
                            variant="contained"
                            color="primary"
                            sx={{
                                padding: "10px 20px",
                                fontSize: { xs: "1rem", sm: "1.2rem" },
                                fontWeight: "bold",
                                borderRadius: 3,
                            }}
                            href="/puppy-application"
                        >
                            Apply for a Puppy
                        </Button>
                    </Box>

                    <ImageList
                        variant="masonry"
                        cols={cols}
                        gap={8}
                        sx={{ maxWidth: "900px", margin: "0 auto" }}
                    >
                        {puppyImages.map((puppy, i) => (
                            <ImageListItem
                                key={i}
                                onClick={() => { setIndex(i); setOpen(true); }}
                                sx={{ cursor: "pointer" }}
                            >
                                <img
                                    src={puppy.src}
                                    alt={puppy.alt}
                                    loading="lazy"
                                    style={{ borderRadius: "8px", width: "100%", display: "block" }}
                                />
                            </ImageListItem>
                        ))}
                    </ImageList>

                    <Lightbox
                        open={open}
                        close={() => setOpen(false)}
                        index={index}
                        slides={puppyImages.map((p) => ({ src: p.src }))}
                    />

                    <Typography
                        variant="body2"
                        sx={{ textAlign: "center", mt: 2, color: "gray" }}
                    >
                        Images cannot be used without Galia Standard Poodle's explicit permission.
                    </Typography>
            </PageLayout>
        </>
    );
};
export default UpcomingLitters;
